import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPrimaryAction,
  DialogSecondaryAction,
  DialogTitle
} from '@/components/UI/Dialog';
import { Typography } from '@/components/UI/Typography';
import { useCoiPermissions } from '@/features/coi/hooks/useCoiPermissions';
import { useHasAnySupplierUpdateApprovePermission } from '@/features/erp-sync';
import { cn } from '@/util/cn';
import PermissionClassification from '@/util/enums/PermissionClassification';
import useBaseCompanyInfo from '@/util/hooks/queries/useBaseCompanyInfo';
import useFeatureFlags from '@/util/hooks/queries/useFeatureFlags';
import useAllowedPermission from '@/util/hooks/useAllowedPermission';
import usePlaygroundDemoRole from '@/util/hooks/usePlaygroundDemoRole';
import { MyCompanyPaths } from '@/util/routes/MyOffice';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';

import { OnboardingSectionPaths } from './SupplierOnboardingRequest/Stepper/sections/onboardingSectionPaths';
import useAddNewCompany from './useAddNewCompany';
import usePlaygroundDemoChromeVisible from './usePlaygroundDemoChromeVisible';

const ERFX_ENDPOINT = import.meta.env.VITE_ERFX_ENDPOINT;
const ONBOARDING_BASE =
  OnboardingSectionPaths.SupplierOnboardingDashboardRequestsSent as string;

type RailLink = {
  label: string;
  href: string;
  /** Path prefixes that mark this entry as the current page. */
  activeOn?: string[];
  /** eRFX is a separate deployable: a plain anchor, never a router Link. */
  external?: boolean;
  allowed: boolean;
};

const isActiveFor = (pathname: string, prefixes: string[]) =>
  prefixes.some((prefix) =>
    prefix === '/'
      ? pathname === '/'
      : pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

/**
 * "Demo Walkthrough" left rail from the design prototype (#9141): the sequence a salesperson
 * walks a prospect through, with the rest of the app under "More (real app areas)". It renders
 * only while QA Playground is ON (user decision, 2026-10-20). Every entry opens the real page,
 * and an entry the user has no permission for is left out entirely, not greyed (same rule as
 * the nav's COI entry, #7751).
 */
const PlaygroundWalkthroughRail = ({ className }: { className?: string }) => {
  const visible = usePlaygroundDemoChromeVisible();
  const { pathname } = useLocation();
  const [role] = usePlaygroundDemoRole();
  const { handleHasPermissionCheckFunction: hasPermission, isLoading } =
    useAllowedPermission();
  const { hasAnyCoiPermission, landingPath: coiLandingPath } = useCoiPermissions();
  const { hasAnyApprovePermission, isLoading: approveLoading } =
    useHasAnySupplierUpdateApprovePermission();
  const { data: currentCompany } = useBaseCompanyInfo();
  const { branchOnboarding } = useFeatureFlags();
  const { start: startNewCompany, isStarting } = useAddNewCompany();
  // Step 2 looks like the navigation steps around it but starts a REAL registration and switches
  // the user out of the demo company, so it is confirmed first (PR !2718 review).
  const [newCompanyConfirmOpen, setNewCompanyConfirmOpen] = useState(false);

  if (!visible) {
    return null;
  }

  const can = (permission: PermissionClassification) =>
    !isLoading && hasPermission(permission);

  const onboardingHref =
    role === 'supplier'
      ? (OnboardingSectionPaths.SupplierOnboardingDashboardRequestsReceived as string)
      : ONBOARDING_BASE;

  // Step 2 ("New Company Sign-Up") is an action, not a link: it is spliced in at render time.
  const steps: RailLink[] = [
    { label: 'Overview', href: '/', activeOn: ['/'], allowed: true },
    {
      label: 'Marketplace',
      href: '/virtual-marketplace',
      activeOn: ['/virtual-marketplace'],
      allowed: true
    },
    {
      label: 'Supplier Onboarding',
      href: onboardingHref,
      activeOn: [ONBOARDING_BASE, '/myoffice/manual-onboarding'],
      allowed: true
    },
    {
      label: 'Compliance & BBBEE',
      href: `/view-profile/${currentCompany?.companyId ?? ''}`,
      activeOn: ['/view-profile'],
      allowed:
        Boolean(currentCompany?.companyId) && can(PermissionClassification.ViewMyProfile)
    },
    {
      label: 'COI Screening',
      href: coiLandingPath ?? '',
      activeOn: ['/myoffice/coi'],
      allowed: hasAnyCoiPermission && Boolean(coiLandingPath)
    },
    {
      label: 'Tenders / eRFX',
      href: `${ERFX_ENDPOINT}/bidder-rfq-listings`,
      external: true,
      allowed: true
    },
    { label: 'Messages', href: '/messaging', activeOn: ['/messaging'], allowed: true }
  ];

  const more: RailLink[] = [
    {
      label: 'Network & Connections',
      href: '/network/suppliers',
      activeOn: ['/network'],
      allowed: true
    },
    {
      label: 'Supplier Updates',
      href: '/pending-approvals',
      activeOn: ['/pending-approvals'],
      allowed: !approveLoading && hasAnyApprovePermission
    },
    {
      label: 'Branch Requests',
      href: '/branch-requests',
      activeOn: ['/branch-requests'],
      allowed:
        Boolean(branchOnboarding) && can(PermissionClassification.ManageBranchApprovals)
    },
    {
      label: 'User Management',
      href: MyCompanyPaths.UserManagement.users,
      activeOn: ['/user-management'],
      allowed: can(PermissionClassification.ManageUsers)
    },
    {
      label: 'Onboarding Configuration',
      href: '/myoffice/configuration',
      activeOn: ['/myoffice/configuration'],
      allowed: can(PermissionClassification.CorporateOnboarding)
    },
    {
      label: 'Company Settings',
      href: '/edit-company-profile',
      activeOn: ['/edit-company-profile'],
      allowed: can(PermissionClassification.EditMyProfile)
    }
  ];

  const numbered = [
    steps[0],
    'new-company' as const,
    ...steps.slice(1).filter((step) => step.allowed)
  ];

  return (
    <nav
      aria-label="Demo Walkthrough"
      data-testid="playground-demo-chrome"
      className={cn(
        'hidden w-[264px] shrink-0 overflow-y-auto border-r border-slate-200 bg-white px-3 pt-[18px] pb-6 lg:block',
        className
      )}
    >
      <p className="px-3 pt-1.5 pb-2 text-[10.5px] font-extrabold tracking-[0.07em] text-[#9AA2B1] uppercase">
        Demo Walkthrough
      </p>
      <ol className="m-0 list-none p-0">
        {numbered.map((step, index) => {
          const number = index + 1;
          const stepClass =
            'mb-0.5 flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13px] font-semibold';

          if (step === 'new-company') {
            return (
              <li key="new-company">
                <button
                  type="button"
                  data-testid="walkthrough-step"
                  onClick={() => setNewCompanyConfirmOpen(true)}
                  disabled={isStarting}
                  className={cn(
                    stepClass,
                    'cursor-pointer text-[#4A5264] hover:bg-[#F5F7FF]'
                  )}
                >
                  <StepNumber number={number} active={false} />
                  New Company Sign-Up
                </button>
              </li>
            );
          }

          const active = !step.external && isActiveFor(pathname, step.activeOn ?? []);
          const className = cn(
            stepClass,
            active
              ? 'bg-primary text-white shadow-[0_2px_6px_rgba(75,105,249,0.25)]'
              : 'text-[#4A5264] hover:bg-[#F5F7FF]'
          );
          const content = (
            <>
              <StepNumber number={number} active={active} />
              {step.label}
            </>
          );

          return (
            <li key={step.label}>
              {step.external ? (
                <a href={step.href} data-testid="walkthrough-step" className={className}>
                  {content}
                </a>
              ) : (
                <Link
                  to={step.href}
                  data-testid="walkthrough-step"
                  aria-current={active ? 'page' : undefined}
                  className={className}
                >
                  {content}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      <p className="mt-5 px-3 pt-1.5 pb-2 text-[10.5px] font-extrabold tracking-[0.07em] text-[#9AA2B1] uppercase">
        More (real app areas)
      </p>
      <ul className="m-0 list-none p-0">
        {more
          .filter((link) => link.allowed)
          .map((link) => {
            const active = isActiveFor(pathname, link.activeOn ?? []);
            return (
              <li key={link.label}>
                <Link
                  to={link.href}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'block w-full rounded-[0.4rem] px-3 py-[7px] text-[12px] font-semibold',
                    active
                      ? 'text-primary bg-[#EEF1FE]'
                      : 'text-[#64748b] hover:bg-[#F5F7FF] hover:text-[#4A5264]'
                  )}
                >
                  {link.label}
                </Link>
              </li>
            );
          })}
      </ul>

      <Dialog open={newCompanyConfirmOpen} onOpenChange={setNewCompanyConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start a real company registration?</DialogTitle>
          </DialogHeader>
          <Typography variant="body2" className="text-muted-foreground">
            This leaves the demo and starts registering a new, real company for your
            account. An in-progress registration is created and you are switched into it -
            it is not demo data and is not removed by Reset demo data.
          </Typography>
          <DialogFooter>
            <DialogSecondaryAction
              type="button"
              variant="outlined"
              onClick={() => setNewCompanyConfirmOpen(false)}
            >
              Cancel
            </DialogSecondaryAction>
            <DialogPrimaryAction
              type="button"
              isLoading={isStarting}
              onClick={() => {
                setNewCompanyConfirmOpen(false);
                void startNewCompany();
              }}
            >
              Start registration
            </DialogPrimaryAction>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </nav>
  );
};

const StepNumber = ({ number, active }: { number: number; active: boolean }) => (
  <span
    aria-hidden="true"
    className={cn(
      'flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-extrabold',
      active ? 'bg-white/25 text-white' : 'bg-[#EEF1F8] text-[#6B7280]'
    )}
  >
    {number}
  </span>
);

export default PlaygroundWalkthroughRail;
